// "KPI seti" alanı — ProjectDetail sağ sütun (TODO-KPI-12): bağlı setin adı (+ çerçevesi),
// "Değiştir" → müşterinin setleri (projenin çerçevesiyle uyumlular önce, "(uyumlu)" eki —
// ProjectNew ile aynı yardımcılar), "Bağı kaldır" → ConfirmDialog → PATCH { kpiSetId: null }.
// Tamamlanmış projede salt okunur (backend 409 project_completed ile hizalı). Snapshot/yanıt
// etkisi yok; sonraki üretim/DOCX yeni seti okur. Kopya TEK kaynak: KPI_PROJECT_COPY.detail.
// Setler `listKpiSets` ile yüklenir; yetki yok/hata → boş liste (ProjectNew kalıbı): set bağlı
// değilse "müşterinin seti yok" notu, bağlıysa yalnız "Bağı kaldır" kalır.

import { useEffect, useMemo, useState } from "react";
import type { Project } from "@mahir/shared";
import { ApiError } from "../../api/client";
import { listKpiSets, type KpiSetWithCount } from "../../api/kpi";
import { updateProject } from "../../api/projects";
import {
  KPI_PROJECT_COPY,
  isProjectKpiSetEditable,
  kpiSetFrameworkWarning,
  kpiSetOptionLabel,
  projectKpiSetLabel,
  sortKpiSetsForFramework,
} from "../../lib/project-kpi-guard";
import { ConfirmDialog } from "../common/ConfirmDialog";

const COPY = KPI_PROJECT_COPY.detail;

interface Props {
  project: Project;
  onUpdated: (project: Project) => void;
}

export function ProjectKpiSetField({ project, onUpdated }: Props): JSX.Element {
  const [sets, setSets] = useState<KpiSetWithCount[]>([]);
  const [setsLoading, setSetsLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [selectedId, setSelectedId] = useState<string>(project.kpiSetId ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unlinkOpen, setUnlinkOpen] = useState(false);

  const editable = isProjectKpiSetEditable(project.status);

  useEffect(() => {
    let cancelled = false;
    setSetsLoading(true);
    listKpiSets(project.clientId)
      .then((list) => {
        if (!cancelled) setSets(list);
      })
      .catch(() => {
        if (!cancelled) setSets([]);
      })
      .finally(() => {
        if (!cancelled) setSetsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [project.clientId]);

  const ordered = useMemo(
    () => sortKpiSetsForFramework(sets, project.frameworkId),
    [sets, project.frameworkId],
  );
  const current = useMemo(
    () => sets.find((s) => s.id === project.kpiSetId) ?? null,
    [sets, project.kpiSetId],
  );
  const selected = useMemo(() => sets.find((s) => s.id === selectedId) ?? null, [sets, selectedId]);
  const warning = editing
    ? kpiSetFrameworkWarning(selected, {
        frameworkId: project.frameworkId,
        frameworkName: project.frameworkName,
      })
    : null;

  async function save(kpiSetId: string | null): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      const updated = await updateProject(project.id, { kpiSetId });
      onUpdated(updated);
      setEditing(false);
      setUnlinkOpen(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : COPY.failed);
    } finally {
      setSaving(false);
    }
  }

  function startEdit(): void {
    setSelectedId(project.kpiSetId ?? "");
    setError(null);
    setEditing(true);
  }

  const smallBtn: React.CSSProperties = {
    color: "var(--color-primary)",
    background: "none",
    border: "none",
    padding: 0,
    fontSize: "0.78rem",
    textDecoration: "underline",
  };

  return (
    <div
      className="rounded-xl p-4 mt-4"
      data-testid="project-kpi-set-field"
      style={{ background: "var(--color-surface)", border: "1px solid var(--color-border)" }}
    >
      <div style={{ color: "var(--color-text-3)", fontSize: "0.72rem" }}>{COPY.heading}</div>
      <div
        className="mt-1"
        style={{ color: "var(--color-text)", fontSize: "0.88rem", fontWeight: 500 }}
      >
        {projectKpiSetLabel(project, current)}
      </div>

      {!editable ? (
        <p className="mt-2 text-xs" style={{ color: "var(--color-text-3)" }}>
          {COPY.readOnly}
        </p>
      ) : editing ? (
        <div className="mt-3">
          <label htmlFor="project-kpi-set-select" className="sr-only">
            {COPY.heading}
          </label>
          <select
            id="project-kpi-set-select"
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            disabled={saving}
            style={{
              width: "100%",
              padding: "8px 10px",
              border: "1px solid var(--color-border)",
              borderRadius: 6,
              fontSize: "0.85rem",
            }}
          >
            <option value="">{KPI_PROJECT_COPY.noneOption}</option>
            {ordered.map((s) => (
              <option key={s.id} value={s.id}>
                {kpiSetOptionLabel(s, project.frameworkId)}
              </option>
            ))}
          </select>
          {warning ? (
            <div
              role="status"
              className="mt-2 px-3 py-2 rounded-lg text-xs"
              style={{
                background: "var(--color-warning-bg)",
                border: "1px solid var(--color-warning-border)",
                color: "var(--color-warning-text)",
              }}
            >
              {warning}
            </div>
          ) : null}
          <p className="mt-2 text-xs" style={{ color: "var(--color-text-3)" }}>
            {COPY.effect}
          </p>
          <div className="mt-2 flex gap-3 items-center">
            <button
              type="button"
              onClick={() => void save(selectedId === "" ? null : selectedId)}
              disabled={saving || selectedId === (project.kpiSetId ?? "")}
              aria-busy={saving}
              className="px-3 py-1.5 rounded-lg text-xs disabled:opacity-50"
              style={{
                background: "var(--color-primary)",
                color: "var(--color-surface)",
                border: "none",
                fontWeight: 600,
              }}
            >
              {saving ? COPY.saving : COPY.save}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              disabled={saving}
              style={smallBtn}
            >
              {COPY.cancel}
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-2 flex gap-3 items-center flex-wrap">
          {setsLoading ? (
            <span style={{ color: "var(--color-text-3)", fontSize: "0.78rem" }}>
              {COPY.loading}
            </span>
          ) : sets.length > 0 ? (
            <button type="button" onClick={startEdit} style={smallBtn}>
              {project.kpiSetId ? COPY.change : COPY.bind}
            </button>
          ) : !project.kpiSetId ? (
            <span style={{ color: "var(--color-text-3)", fontSize: "0.78rem" }}>{COPY.noSets}</span>
          ) : null}
          {project.kpiSetId ? (
            <button type="button" onClick={() => setUnlinkOpen(true)} style={smallBtn}>
              {COPY.unlink}
            </button>
          ) : null}
        </div>
      )}

      {error ? (
        <p role="alert" className="mt-2 text-xs" style={{ color: "var(--color-error)" }}>
          {error}
        </p>
      ) : null}

      <ConfirmDialog
        open={unlinkOpen}
        title={COPY.unlinkTitle}
        body={COPY.unlinkBody}
        confirmLabel={COPY.unlink}
        cancelLabel={COPY.cancel}
        tone="neutral"
        busy={saving}
        onConfirm={() => void save(null)}
        onCancel={() => setUnlinkOpen(false)}
      />
    </div>
  );
}
